import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import Login from './Login';
import Chat from './Chat';
import Layout from './Layout';
import LoadingSpinner from './LoadingSpinner';
import ResetPassword from './ResetPassword';
import { callFunction } from '../lib/functionCaller';
import MidiBackground from './MidiBackground';
import OnboardingCarousel from './OnboardingCarousel';
import PricingPage from './PricingPage';
import { logConversion, ADS_CONVERSIONS } from '../lib/amplitude';
import { readFirstTouch } from '../lib/firstTouch';
import { useNavigate } from 'react-router-dom';

const PLAN_PRICES = { Basic: 4.99, Musician: 9.99, Producer: 19.99 };
const TOPUP_PRICE = 4.99;
// What the user went to Stripe to buy, kept in localStorage (not sessionStorage: iOS
// Safari can bring them back in a new tab) until the purchase is reported to Amplitude,
// Google Ads and Meta. Normally that happens on the return from Checkout; if that
// landing never fires, the next app load reports it once the profile shows the purchase.
const PENDING_PURCHASE_KEY = 'midimate_pending_purchase';
const PENDING_PURCHASE_MAX_AGE = 24 * 60 * 60 * 1000;

const readPendingPurchase = () => {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_PURCHASE_KEY));
    if (pending && Date.now() - (pending.at || 0) < PENDING_PURCHASE_MAX_AGE) return pending;
  } catch (e) {
    // storage unavailable: the purchase is still reported from the URL, without plan details
  }
  return null;
};

const clearPendingPurchase = () => {
  try { localStorage.removeItem(PENDING_PURCHASE_KEY); } catch (e) { /* ignore */ }
};

const reportPurchase = (pending, sessionId, user) => {
  const isTrial = !!pending.trial;
  // A trial start is reported as a purchase with no value so Ads can optimise for it
  const eventName = isTrial ? 'Trial Started' : pending.planName ? 'Subscription Purchase Completed' : 'Top Up Purchase Completed';
  logConversion(eventName, ADS_CONVERSIONS.purchaseCompleted, {
    transaction_id: sessionId,
    value: isTrial ? 0 : pending.value,
    currency: 'USD',
    planName: pending.planName,
    credits: pending.credits,
    trial: isTrial,
    source: pending.source,
    email: user?.email,
  });
};

const subActive = (user) => {
  return user.has_active_subscription && ( user.subscription_end_date ? new Date(user.subscription_end_date) > new Date() : true );
};

// A free trial is offered once, and only to users who have never had a Stripe subscription
const trialEligibleFor = (user) => {
  return !subActive(user) && !user.trial_used_at && !user.stripe_customer_id;
};

const trialingFor = (user) => {
  return subActive(user) && !!user.trial_ends_at && new Date(user.trial_ends_at) > new Date();
};

function AppContent({ initialView }) {
  const { user, loading: authLoading, signOut } = useAuth();
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true); // New state
  const [isResetPasswordFlow, setIsResetPasswordFlow] = useState(false);
  const [isSubscriptionActive, setIsSubscriptionActive] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(true);
  const [userPlan, setUserPlan] = useState('');
  const [trialEligible, setTrialEligible] = useState(false);
  const [isTrialing, setIsTrialing] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const fetchUserProfile = useCallback(async () => {
    if (user) {
      setCreditsLoading(true);
      setSubscriptionLoading(true); // Set subscription loading to true
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select(`
            credits, 
            has_active_subscription, 
            subscription_end_date, 
            cancel_at_period_end, 
            has_completed_onboarding,
            subscription_tier,
            trial_used_at,
            trial_ends_at,
            stripe_customer_id,
            timezone
          `)
          .eq('id', user.id)
          .single();

        if (error) throw error;
        // Remember the browser's timezone so emails land in daytime hours
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz && tz !== data.timezone) {
            supabase.rpc('set_timezone', { tz }).then(({ error: tzError }) => {
              if (tzError) console.error('set_timezone', tzError);
            });
          }
        } catch (e) {
          // no Intl support; the email falls back to the sign-up hour
        }
        // The return from Checkout did not report the purchase (tab closed on Stripe's
        // page, session lost in an in-app browser): report it now that it shows here
        const pending = readPendingPurchase();
        if (pending && !new URLSearchParams(window.location.search).has(pending.planName ? 'session_id' : 'topup_session_id')) {
          const landed = pending.planName ? subActive(data) : data.credits >= (pending.creditsBefore || 0) + (pending.credits || 0);
          if (landed) {
            clearPendingPurchase();
            reportPurchase(pending, pending.id, user);
          }
        }
        setCredits(data.credits);
        setIsSubscriptionActive(subActive(data));
        setCancelAtPeriodEnd(data.cancel_at_period_end);
        setHasCompletedOnboarding(data.has_completed_onboarding);
        setUserPlan(data.subscription_tier);
        setTrialEligible(trialEligibleFor(data));
        setIsTrialing(trialingFor(data));
        console.log('userPlan', data.subscription_tier);
        // Show onboarding if user hasn't completed it
        if (!data.has_completed_onboarding) {
          setShowOnboarding(true);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setCreditsLoading(false);
        setSubscriptionLoading(false); // Set subscription loading to false
      }
    } else {
      setCreditsLoading(false);
      setSubscriptionLoading(false); // Set subscription loading to false
    }
  }, [user]);

  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Stripe sends the user back with a session id in the URL after a completed checkout
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const topUpSessionId = params.get('topup_session_id');
    if (!sessionId && !topUpSessionId) return;

    const pending = readPendingPurchase() || { trial: params.get('trial') === '1', planName: sessionId ? 'unknown' : undefined };
    clearPendingPurchase();
    reportPurchase(pending, sessionId || topUpSessionId, user);

    // Remove the session id so a refresh does not log the purchase again
    window.history.replaceState({}, '', window.location.pathname);
    // The webhook that credits the account may land a moment after the redirect
    const refresh = setTimeout(fetchUserProfile, 4000);
    return () => clearTimeout(refresh);
  }, [user, fetchUserProfile]);

  // A profile that has not finished onboarding is a newly created account
  useEffect(() => {
    if (!user || hasCompletedOnboarding) return;
    const key = `midimate_account_created_logged_${user.id}`;
    try {
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch (e) {
      // Ignore storage errors; worst case the event repeats until onboarding is completed
    }
    logConversion('Account Created', ADS_CONVERSIONS.accountCreated, {
      method: user.app_metadata?.provider,
    });
    // Hand the first visit (landing page, referrer) to the profile now that there is one
    const first = readFirstTouch();
    if (first) {
      supabase.rpc('set_first_touch', { url: first.url, referrer: first.referrer, seen_at: first.at })
        .then(({ error }) => { if (error) console.error('set_first_touch', error); });
    }
  }, [user, hasCompletedOnboarding]);
  const handleOnboardingComplete = async () => {
    setShowOnboarding(false);
    if (user) {
      try {
        const { data, error } = await supabase.rpc('update_onboarding_status', { user_id: user.id });
        if (error) throw error;
        setHasCompletedOnboarding(true);
      } catch (error) {
        console.error('Error in handleOnboardingComplete:', error);
      }
    } else {
      console.error('No user found when trying to complete onboarding');
    }
  };

  const updateCredits = useCallback((newCredits) => {
    setCredits(newCredits);
  }, []);

  const rememberPendingPurchase = (purchase) => {
    try {
      localStorage.setItem(PENDING_PURCHASE_KEY, JSON.stringify({ ...purchase, at: Date.now() }));
    } catch (e) {
      // Ignore storage errors; the purchase is still logged without plan details
    }
  };

  const handleManageBilling = async (path = '') => {
    try {
      const data = await callFunction('create-billing-portal-session', { userId: user.id });
      window.location.href = data.url + path;
    } catch (error) {
      console.error('Error creating billing portal session');
      throw error;
    }
  };

  // `source` names the prompt that sent the user to Checkout (download_wall, out_of_credits,
  // post_download_offer, pricing_page, ...); it lands in the Stripe session's metadata so a
  // purchase can be traced even when the client's analytics are blocked
  const handleSubscription = async (planName, source = 'unknown') => {
    if (isSubscriptionActive) {
      return handleManageBilling();
    } else if (!planName) {
      // No plan chosen yet (header button, credits popover): let the user pick one
      navigate('/pricing');
    } else {
      try {
        const data = await callFunction('create-checkout-session', {
          userId: user.id,
          planName: planName,
          source,
        });
        rememberPendingPurchase({ id: data.id, planName, value: PLAN_PRICES[planName], trial: !!data.trial, source });
        window.location.href = data.url;
      } catch (error) {
        console.error('Error creating checkout session:', error);
        throw error;
      }
    }
  };

  const handleTopUpPurchase = async (tokens, source = 'unknown') => {
    if (!user) {
      console.error('User not logged in');
      return;
    }

    try {
      const data = await callFunction('create-topup-session', {
        userId: user.id,
        credits: tokens,
        source,
      });
      rememberPendingPurchase({ id: data.id, credits: tokens, value: TOPUP_PRICE, creditsBefore: credits, source });
      window.location.href = data.url;
    } catch (error) {
      console.error('Error creating top-up session:', error);
      // You might want to show an error message to the user here
    }
  };

  if (authLoading) {
    return <LoadingSpinner />;
  }
  
  if (initialView === "pricing" && user) {
    return (
      <div className="full-viewport-height flex flex-col overflow-hidden">
        <MidiBackground isApp={true}/>
        <div className="relative z-10 flex-grow flex flex-col overflow-hidden">
          <PricingPage 
            onPurchaseSubscription={handleSubscription}
            isSubscriptionActive={isSubscriptionActive}
            onManageBilling={handleManageBilling}
            userPlan={userPlan}
            trialEligible={trialEligible}
            isTrialing={isTrialing}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="full-viewport-height flex flex-col overflow-hidden">
      <MidiBackground isApp={true}/>
      <div className="relative z-10 flex-grow flex flex-col overflow-hidden">
        {isResetPasswordFlow ? (
          <ResetPassword onResetComplete={() => {
            setIsResetPasswordFlow(false);
            signOut();
          }} />
        ) : user ? (
          <Layout 
            user={user} 
            credits={credits} 
            creditsLoading={creditsLoading}
            onPurchaseSubscription={handleSubscription} 
            onManageBilling={handleManageBilling}
            isResetPasswordFlow={isResetPasswordFlow}
            isSubscriptionActive={isSubscriptionActive}
            cancelAtPeriodEnd={cancelAtPeriodEnd}
            setShowOnboarding={setShowOnboarding}
            subscriptionLoading={subscriptionLoading}
            userPlan={userPlan}
            trialEligible={trialEligible}
            isTrialing={isTrialing}
            updateCredits={updateCredits}
            onTopUpPurchase={handleTopUpPurchase}
          />
        ) : (
          <Login />
        )}
      </div>
      {showOnboarding && (
        <OnboardingCarousel onClose={handleOnboardingComplete} />
      )}
    </div>
  );
}

export default AppContent;
